<script setup lang="ts">
import BaseButton from "~/components/Base/BaseButton.vue";
// import BaseRating from "~/components/Base/BaseRating.vue";
import { useMainStore, useProductPageStore } from "~/stores/main";
import { useCartStore } from "~/stores/cartStore";
import { usePriceFormatter } from "~/utils/priceFormatter";
import {
  addToCart as sendAddToCartEvent,
  viewItem as sendViewItemEvent,
} from "~/composables/useGoogleAnalytics";

// -----------------------
// composables
// -----------------------
const { status, data } = useAuth();
const store = useProductPageStore();
const mainStore = useMainStore();
const cartStore = useCartStore();
const { totalStockOfVariant, getAssetUrl, isRealContent } = useHelpers();
const { t } = useI18n();
const config = useRuntimeConfig();
const themeGroup = config.public.theme.group;

// -----------------------
// reactive properties
// -----------------------
const isSizeGuideModalVisibleRef = ref<boolean>(false);
// const isStoreStockModalVisibleRef = ref<boolean>(false);
const addingToBasketRef = ref<boolean>(false); // todo: put some spinner or similar to "add to basket" button based on this
const quantityRef = ref<number>(1);
// const isProductFavourite = ref<boolean>(false);
const isAddToCartButtonShaking = ref<boolean>(false);

// -----------------------
// computed properties
// -----------------------
const variantRef = computed(() => {
  if (store.size === null || store.color === null) {
    return null;
  }

  return store.masterProduct?.products.find((variant: Record<any, any>) => {
    return (
      variant.product.product_meret_property === store.size &&
      variant.product.product_szin_property === store.color
    );
  });
});

const maxQuantityRef = computed(() => {
  if (variantRef.value) {
    return totalStockOfVariant(variantRef.value);
  }

  return 0;
});

const priceRef = computed(() => {
  if (variantRef.value) {
    // console.log(variantRef.value);
    if (status.value === "authenticated") {
      if (
        Array.isArray(data?.value?.usergroups) &&
        data?.value?.usergroups?.includes(1) &&
        Array.isArray(variantRef.value.product.prices) &&
        variantRef.value.product.prices.length > 0
      ) {
        return usePriceFormatter(
          {
            original_price: parseInt(variantRef.value.product.original_price),
            price: parseInt(variantRef.value.product.price),
            pricelist_original_price: parseInt(
              variantRef.value.product.prices[0].original_price,
            ),
            pricelist_price: parseInt(variantRef.value.product.prices[0].price),
          },
          "product",
        );
      }
    }

    return usePriceFormatter(
      {
        original_price: parseInt(variantRef.value.product.original_price),
        price: parseInt(variantRef.value.product.price),
      },
      "product",
    );
  }

  return usePriceFormatter(store.masterProduct, "masterProduct");
});
/*
const favouriteIconClass = computed(() => {
  return isProductFavourite.value
    ? "m-icon-favourite_filled"
    : "m-icon-favourite";
});
*/
const manufacturerLogo = computed(() => {
  const folder = config.public.routes.manufacturers;

  return store.manufacturer?.logo
    ? getAssetUrl(`Public/assets/webshop/${folder}/${store.manufacturer?.logo}`)
    : "";
});

const manufacturerSizechart = computed(() => {
  return store.manufacturer?.sizechart
    ? getAssetUrl(`Public/assets/${store.manufacturer?.sizechart}`)
    : "";
});

const isAddToCartDisabled = computed(() => {
  return (
    !store.color ||
    !store.size ||
    maxQuantityRef.value === 0 ||
    addingToBasketRef.value
  );
});

const addToCartButtonText = computed(() => {
  if (isAddToCartButtonShaking.value) {
    if (!store.size) {
      return t("product_page.select_size");
    } else if (maxQuantityRef.value === 0) {
      return t("out_of_stock");
    } else {
      return t("add_to_cart");
    }
  } else {
    return t("add_to_cart");
  }
});

const hasDescription = computed(() => {
  const description = store.masterProduct.description;

  return (
    description && typeof description === "string" && isRealContent(description)
  );
});

// -----------------------
// helper functions
// -----------------------

const addToBasket = async () => {
  // console.log('adding to basket');
  addingToBasketRef.value = true;
  if (store.selectedVariant) {
    // FIXME: quantity is not updated when cart panel is opened
    const added = await cartStore.addProduct(
      store.selectedVariant,
      quantityRef.value,
    );
    if (added) {
      sendAddToCartEvent(
        store.masterProduct,
        variantRef.value,
        quantityRef.value,
      );
      addingToBasketRef.value = false;
      await cartStore.fetchCartData(true);
      mainStore.cartVisible = true;
    }
  }
  addingToBasketRef.value = false;
};

/*
const toggleFavourite = () => {
  // TODO: add BE logic here
  isProductFavourite.value = !isProductFavourite.value;
};
*/

const urgeUserToSelect = () => {
  isAddToCartButtonShaking.value = true;

  setTimeout(() => {
    isAddToCartButtonShaking.value = false;
  }, 1000);
};
</script>

<template>
  <div class="col-span-2 lg:col-span-1 px-4 pt-5">
    <BaseModal
      :width="700"
      :height="400"
      :show="isSizeGuideModalVisibleRef"
      @close="isSizeGuideModalVisibleRef = false"
    >
      <NuxtImg :src="manufacturerSizechart" format="webp" loading="lazy" />
    </BaseModal>

    <!-- Manufacturer & product name -->
    <div class="flex justify-between items-center">
      <div v-if="themeGroup === 'mountex'" class="text-brand-headline">
        <h1 class="!text-2xl lg:!text-3xl !mt-[0] font-bold mb-1">
          {{ store.masterProduct?.masterproduct_gyarto_property }}
        </h1>
        <h2 class="!text-base lg:!text-2xl !mt-0">
          {{ store.masterProduct?.name }}
        </h2>
      </div>
      <h1 v-else class="font-bold !text-2xl lg:!text-3xl xl:!text-4xl !mt-[0]">
        {{ store.masterProduct?.name }}
      </h1>

      <div v-if="store.manufacturer && manufacturerLogo" class="ml-[50px]">
        <NuxtImg
          v-if="config.public.theme.group === 'tnf'"
          :alt="store.manufacturer.name"
          :src="manufacturerLogo"
          loading="lazy"
          width="120"
        />
        <a
          v-else
          :href="`/${config.public.routes.manufacturers}/${store.manufacturer.slug}`"
        >
          <NuxtImg
            class="cursor-pointer"
            :alt="store.manufacturer.name"
            :src="manufacturerLogo"
            loading="lazy"
            width="120"
          />
        </a>
      </div>
    </div>

    <div class="!text-xs lg:!text-base">
      <!-- Rating: TBD -->
      <!-- <BaseRating :rating="4" /> -->

      <!-- SKU -->
      <div
        v-if="store.masterProduct?.stock"
        class="pt-1 mb-1 lg:pt-2 xl:pt-2.5 lg:mb-3 xl:mb-2"
      >
        {{ t("sku") }}:
        {{ store.selectedVariantSku || t("product_page.select_size") }}
      </div>

      <!-- Price(s) -->
      <div
        v-if="store.masterProduct?.stock"
        class="text-xl lg:text-2xl leading-7"
      >
        <!-- eslint-disable vue/no-v-html -->
        <span v-html="priceRef" />
        <!-- eslint-enable -->
      </div>
    </div>

    <ProductPageVariants
      :size-guide="manufacturerSizechart !== ''"
      @show-size-guide="isSizeGuideModalVisibleRef = true"
      @load="sendViewItemEvent"
      @select="sendViewItemEvent"
    ></ProductPageVariants>

    <!-- Cart button -->
    <div
      v-if="store.masterProduct?.stock ?? 0 > 0"
      class="add-to-cart-container relative flex justify-center h-[50px] lg:h-[56px]"
    >
      <BaseButton
        type="primary"
        class="add-to-cart text-xl px-[60px] bg-brand-search min-h-[50px] lg:min-h-[56px]"
        :class="{ shaking: isAddToCartButtonShaking }"
        :disabled="isAddToCartDisabled"
        @click="addToBasket()"
        >{{ addToCartButtonText }}</BaseButton
      >
      <div
        v-if="isAddToCartDisabled"
        class="absolute w-full h-full cursor-not-allowed z-20 top-0"
        @click.capture="urgeUserToSelect"
      ></div>
    </div>
    <div v-else>
      <BaseButton type="green" class="text-xl mt-[25px]" :disabled="true">{{
        t("non_orderable")
      }}</BaseButton>
    </div>
    <div v-if="store.masterProduct?.stock ?? 0 > 0">
      <ProductPageStockInfo />
      <ProductPageShippingDetails />
    </div>
    <div v-else class="mt-5 text-brand-highlight">
      <span class="m-icon-cancel mr-5" />{{ t("out_of_stock") }}
    </div>
    <ProductPageDescription v-if="hasDescription" />
    <ProductPageAttributes />
  </div>
</template>

<style scoped>
@keyframes horizontal-shaking {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(7px);
  }
  50% {
    transform: translateX(-7px);
  }
  75% {
    transform: translateX(7px);
  }
  100% {
    transform: translateX(0);
  }
}

.add-to-cart.shaking {
  animation-name: horizontal-shaking;
  animation-duration: 400ms;
  animation-delay: 200ms;
  animation-iteration-count: 2;
}
.add-to-cart-container,
.add-to-cart:not(:disabled) {
  transition-property: width, height;
  transition-duration: 500ms;
}

.add-to-cart {
  width: 100%;
}
</style>
